import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createTestimonial, updateTestimonial, getTestimonialById } from "../api/testimonials";
import { CreateTestimonialDto, UpdateTestimonialDto } from "../types/testimonial";
import {
	Box,
	Button,
	TextField,
	Typography,
	Paper,
	FormControlLabel,
	Switch,
	Rating,
	Alert,
	CircularProgress,
	Snackbar,
	Card,
	CardContent,
	Divider,
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';

export default function CreateTestimonialPage() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const isEditing = !!id;

	const [formData, setFormData] = useState<CreateTestimonialDto>({
		content: { en: "", ru: "" },
		clientName: { en: "", ru: "" },
		company: { en: "", ru: "" },
		rating: 5,
		featured: false,
		active: true,
	});

	const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
		open: false,
		message: "",
		severity: "success"
	});

	// Fetch testimonial data if editing
	const { data: testimonial, isLoading: isLoadingTestimonial } = useQuery({
		queryKey: ["testimonial", id],
		queryFn: () => getTestimonialById(id!),
		enabled: isEditing,
	});

	// Update form data when testimonial is loaded
	useEffect(() => {
		if (testimonial) {
			setFormData({
				content: testimonial.content,
				clientName: testimonial.clientName,
				company: testimonial.company,
				rating: testimonial.rating,
				featured: testimonial.featured,
				active: testimonial.active,
			});
		}
	}, [testimonial]);

	// Create mutation
	const createMutation = useMutation({
		mutationFn: createTestimonial,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["testimonials"] });
			setSnackbar({
				open: true,
				message: "Testimonial created successfully",
				severity: "success"
			});
			setTimeout(() => navigate("/dashboard/testimonials"), 1500);
		},
		onError: () => {
			setSnackbar({
				open: true,
				message: "Failed to create testimonial",
				severity: "error"
			});
		}
	});

	// Update mutation
	const updateMutation = useMutation({
		mutationFn: ({ id, data }: { id: string; data: UpdateTestimonialDto }) =>
			updateTestimonial(id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["testimonials"] });
			queryClient.invalidateQueries({ queryKey: ["testimonial", id] });
			setSnackbar({
				open: true,
				message: "Testimonial updated successfully",
				severity: "success"
			});
			setTimeout(() => navigate("/dashboard/testimonials"), 1500);
		},
		onError: () => {
			setSnackbar({
				open: true,
				message: "Failed to update testimonial",
				severity: "error"
			});
		}
	});

	const handleInputChange = (field: string, language: 'en' | 'ru', value: string) => {
		setFormData(prev => ({
			...prev,
			[field]: {
				...prev[field as keyof CreateTestimonialDto] as { en: string; ru: string },
				[language]: value
			}
		}));
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		if (isEditing) {
			updateMutation.mutate({ id: id!, data: formData });
		} else {
			createMutation.mutate(formData);
		}
	};

	const handleBack = () => {
		navigate("/dashboard/testimonials");
	};

	if (isEditing && isLoadingTestimonial) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Box p={3}>
			<Box display="flex" alignItems="center" mb={3}>
				<Button
					startIcon={<ArrowBackIcon />}
					onClick={handleBack}
					sx={{ mr: 2 }}
				>
					Back
				</Button>
				<Typography variant="h4" component="h1">
					{isEditing ? "Edit Testimonial" : "Create New Testimonial"}
				</Typography>
			</Box>

			<Paper sx={{ p: 3 }}>
				<form onSubmit={handleSubmit}>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
						{/* Client Name */}
						<Box>
							<Typography variant="h6" gutterBottom>
								Client Name
							</Typography>
							<Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
								<TextField
									fullWidth
									label="Client Name (English)"
									value={formData.clientName.en}
									onChange={(e) => handleInputChange('clientName', 'en', e.target.value)}
									required
								/>
								<TextField
									fullWidth
									label="Client Name (Russian)"
									value={formData.clientName.ru}
									onChange={(e) => handleInputChange('clientName', 'ru', e.target.value)}
									required
								/>
							</Box>
						</Box>

						{/* Company */}
						<Box>
							<Typography variant="h6" gutterBottom>
								Company
							</Typography>
							<Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
								<TextField
									fullWidth
									label="Company (English)"
									value={formData.company.en}
									onChange={(e) => handleInputChange('company', 'en', e.target.value)}
									required
								/>
								<TextField
									fullWidth
									label="Company (Russian)"
									value={formData.company.ru}
									onChange={(e) => handleInputChange('company', 'ru', e.target.value)}
									required
								/>
							</Box>
						</Box>

						{/* Content */}
						<Box>
							<Typography variant="h6" gutterBottom>
								Testimonial Content
							</Typography>
							<Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
								<TextField
									fullWidth
									multiline
									rows={4}
									label="Content (English)"
									value={formData.content.en}
									onChange={(e) => handleInputChange('content', 'en', e.target.value)}
									required
								/>
								<TextField
									fullWidth
									multiline
									rows={4}
									label="Content (Russian)"
									value={formData.content.ru}
									onChange={(e) => handleInputChange('content', 'ru', e.target.value)}
									required
								/>
							</Box>
						</Box>

						{/* Rating */}
						<Box>
							<Typography variant="h6" gutterBottom>
								Rating
							</Typography>
							<Rating
								value={formData.rating}
								onChange={(_, newValue) => {
									setFormData(prev => ({ ...prev, rating: newValue || 5 }));
								}}
								size="large"
							/>
						</Box>

						{/* Switches */}
						<Box>
							<Card variant="outlined">
								<CardContent>
									<Typography variant="h6" gutterBottom>
										Settings
									</Typography>
									<Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
										<FormControlLabel
											control={
												<Switch
													checked={formData.featured}
													onChange={(e) => setFormData(prev => ({ ...prev, featured: e.target.checked }))}
												/>
											}
											label="Featured Testimonial"
										/>
										<FormControlLabel
											control={
												<Switch
													checked={formData.active}
													onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
												/>
											}
											label="Active"
										/>
									</Box>
								</CardContent>
							</Card>
						</Box>

						{/* Submit Button */}
						<Box>
							<Box display="flex" gap={2} justifyContent="flex-end">
								<Button
									variant="outlined"
									onClick={handleBack}
								>
									Cancel
								</Button>
								<Button
									type="submit"
									variant="contained"
									startIcon={<SaveIcon />}
									disabled={createMutation.isPending || updateMutation.isPending}
								>
									{createMutation.isPending || updateMutation.isPending ? (
										<CircularProgress size={20} />
									) : (
										isEditing ? "Update Testimonial" : "Create Testimonial"
									)}
								</Button>
							</Box>
						</Box>
					</Box>
				</form>
			</Paper>

			{/* Snackbar */}
			<Snackbar
				open={snackbar.open}
				autoHideDuration={6000}
				onClose={() => setSnackbar({ ...snackbar, open: false })}
			>
				<Alert
					onClose={() => setSnackbar({ ...snackbar, open: false })}
					severity={snackbar.severity}
					sx={{ width: '100%' }}
				>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</Box>
	);
} 